import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

import styles from './styles.module.scss';

import { LandingSuccessStoriesType } from '@utils/types/landings';
import ImageWithLoader from '@components/_shared/imageWithLoader';
import { CircledChevronLeft, CircledChevronRight } from '@assets/icons';
import { colorsByTheme } from '@utils/constants/colors';
import { splitByCommas } from '@utils/helpers';

const SuccessStories = (props: LandingSuccessStoriesType) => {
  const { title, description, items } = props;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const slidesWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const nextSlide = () => {
      if (currentSlideIndex === items.length - 1) {
        setCurrentSlideIndex(0);
      } else {
        setCurrentSlideIndex(currentSlideIndex + 1);
      }
    };

    timer = setInterval(nextSlide, 10000);

    return () => {
      clearInterval(timer);
    };
  }, [currentSlideIndex]);

  useEffect(() => {
    if (slidesWrapperRef.current) {
      slidesWrapperRef.current.style.transform = `translateX(-${
        currentSlideIndex * 100
      }%)`;
    }
  }, [currentSlideIndex]);

  const onBackClick = () => {
    setCurrentSlideIndex(currentSlideIndex - 1);
  };

  const onNextClick = () => {
    setCurrentSlideIndex(currentSlideIndex + 1);
  };

  return (
    <div className={styles.successStories}>
      <div className={styles.successStories__wrapper}>
        <div className={styles.successStories__header}>
          <h3 className={styles.successStories__title}>{title}</h3>
          <div className={styles.arrows}>
            <CircledChevronLeft
              className={clsx([
                styles.arrow,
                currentSlideIndex === 0 && styles.arrow__disabled,
              ])}
              stroke={colorsByTheme['dark'].textPrimaryColor}
              onClick={currentSlideIndex !== 0 ? onBackClick : undefined}
            />
            <CircledChevronRight
              className={clsx([
                styles.arrow,
                currentSlideIndex === items.length - 1 &&
                  styles.arrow__disabled,
              ])}
              stroke={colorsByTheme['dark'].textPrimaryColor}
              onClick={
                currentSlideIndex !== items.length - 1 ? onNextClick : undefined
              }
            />
          </div>
        </div>

        <p className={styles.successStories__description}>{description}</p>

        <div className={styles.slides}>
          <div className={styles.slides__wrapper} ref={slidesWrapperRef}>
            {items.map((slide, index) => (
              <div
                className={`${styles.slide} ${
                  index === currentSlideIndex ? styles.slide__active : ''
                }`}
                key={index}
                onClick={() => setCurrentSlideIndex(index)}
                style={{
                  transform: `scale(${index === currentSlideIndex ? 1 : 0.85})`,
                  opacity: index === currentSlideIndex ? 1 : 0.7,
                  transition:
                    'transform 0.7s cubic-bezier(0.175, 0.885, 0.32, 1), opacity 0.7s ease',
                }}
              >
                <div className={styles.slideInfo}>
                  <h5 className={styles.slideInfo__location}>
                    {slide.location}
                  </h5>
                  <h4 className={styles.slideInfo__property}>
                    {slide.property}
                  </h4>
                  <div className={styles.slideInfo__stats}>
                    <div>
                      <div className={styles.slideInfo__statsLabel}>
                        Yearly Rental Revenue
                      </div>
                      <div className={styles.slideInfo__statsDivider} />
                      <div className={styles.slideInfo__statsValues}>
                        <div className={styles.slideInfo__block}>
                          <div className={styles.slideInfo__beforeValue}>
                            ${splitByCommas(slide.revenueBeforeRove)}
                          </div>
                          <div className={styles.slideInfo__beforelabel}>
                            before ROVE
                          </div>
                        </div>
                        <div className={styles.slideInfo__block}>
                          <div className={styles.slideInfo__afterValue}>
                            ${splitByCommas(slide.revenueAfterRove)}
                          </div>
                          <div className={styles.slideInfo__afterLabel}>
                            with ROVE
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.slideInfo__pricing}>
                      <div className={styles.slideInfo__pricingPremiumValue}>
                        {(
                          ((slide.revenueAfterRove - slide.revenueBeforeRove) /
                            slide.revenueBeforeRove) *
                          100
                        ).toFixed(1)}
                        %
                      </div>
                      <div className={styles.slideInfo__pricingPremiumLabel}>
                        Pricing premium
                      </div>
                    </div>
                  </div>

                  <div className={styles.slideInfo__quote}>“{slide.quote}”</div>

                  <div className={styles.slideInfo__authorWrapper}>
                    {slide.ownerImage && (
                      <ImageWithLoader
                        containerStyle={styles.slideInfo__avatar}
                        src={slide.ownerImage}
                        alt="author avatar"
                        sizes="48px"
                      />
                    )}

                    <div className={styles.slideInfo__author}>
                      {slide.owner}
                    </div>
                  </div>
                </div>

                <ImageWithLoader
                  containerStyle={styles.slide__image}
                  src={slide.image}
                  alt={slide.property}
                  sizes="66vw"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessStories;
